import React from 'react'
import PropTypes from 'prop-types'
import {
  Field,
  FieldError,
  FormHint,
  FormLabel,
  MethodOption,
  MethodOptions,
  Root,
  UtmInputWrap,
} from './PensionConfigForm.styles'
import { Input } from '../../components'
import {
  CONFIGURABLE_METHODS,
  METHOD,
  METHOD_DETAILS,
  validateUtmQuantity,
} from '../../config/settings/constants'
import { formatCurrency } from '../../config/resources/utils'

/**
 * Validates the pension value for the selected calculation method.
 * @returns {{ valid: boolean, error: string }}
 */
export const validatePensionValue = (
  method,
  percentage,
  amount,
  { allowFixedAmount = false } = {}
) => {
  if (method === METHOD.UTM) {
    const raw = amount !== '' && amount != null ? amount : percentage
    return validateUtmQuantity(raw)
  }

  if (method === METHOD.IMM) {
    const raw = percentage !== '' && percentage != null ? percentage : amount
    const parsed = Number(raw)
    if (raw === '' || raw == null || Number.isNaN(parsed)) {
      return { valid: false, error: 'Ingresa el porcentaje del sueldo mínimo.' }
    }
    if (parsed <= 0) {
      return { valid: false, error: 'El porcentaje debe ser mayor a 0.' }
    }
    if (parsed > 100) {
      return { valid: false, error: 'El porcentaje no puede ser mayor a 100.' }
    }
  }

  if (method === METHOD.FIXED_AMOUNT) {
    if (!allowFixedAmount) {
      return {
        valid: false,
        error:
          'El monto fijo en pesos ya no se configura aquí. Usa UTM o el % del sueldo mínimo.',
      }
    }
    const raw = amount !== '' && amount != null ? amount : percentage
    if (raw === '' || raw == null) {
      return { valid: false, error: 'Ingresa el monto en pesos.' }
    }
    const parsed = Number(String(raw).trim().replace(',', '.'))
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return { valid: false, error: 'El monto en pesos debe ser mayor a 0.' }
    }
    return { valid: true, error: '' }
  }

  return { valid: true, error: '' }
}

export const validatePaymentDay = (day) => {
  if (day === '' || day == null) {
    return { valid: false, error: 'Elige el día de pago.' }
  }
  const parsed = Number(day)
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 31) {
    return {
      valid: false,
      error: 'Elige un día entre 1 y 31.',
    }
  }
  return { valid: true, error: '' }
}

const selectableMethods = METHOD_DETAILS.filter((item) =>
  CONFIGURABLE_METHODS.includes(item.value)
)

const PensionConfigForm = ({
  method,
  percentage,
  amount,
  onMethodChange,
  onPercentageChange,
  onAmountChange,
  methodError,
  valueError,
  showMethod,
  showMethodLabel,
  showValue,
  immValue,
  disabled,
  valueInputFullWidth,
  showValueLabel,
  showValueHint,
}) => {
  const utmValue = amount ?? ''
  const immPct = percentage ?? ''

  const isUtm = method === METHOD.UTM
  const isImm = method === METHOD.IMM

  const estimatedImmAmount =
    isImm && immValue && Number(immPct) > 0
      ? Math.round((Number(immValue) * Number(immPct)) / 100)
      : null

  return (
    <Root>
      {showMethod && (
        <Field>
          {showMethodLabel && (
            <FormLabel id="pension-method-label">Tipo de reajuste</FormLabel>
          )}

          <MethodOptions role="radiogroup" aria-labelledby={showMethodLabel ? 'pension-method-label' : undefined}>
            {selectableMethods.map((item) => {
              const active = method === item.value
              return (
                <MethodOption
                  key={item.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  $active={active}
                  disabled={disabled}
                  onClick={() => onMethodChange(item.value)}
                >
                  <strong>{item.label}</strong>
                  <span>{item.description}</span>
                </MethodOption>
              )
            })}
          </MethodOptions>

          {methodError && <FieldError>{methodError}</FieldError>}
        </Field>
      )}

      {showValue && isUtm && (
        <Field>
          {showValueLabel && <FormLabel>UTM de tu resolución</FormLabel>}
          {showValueHint && (
            <FormHint>
              Por ejemplo, si tu resolución indica 2,05 UTM, ingresa 2,05.
            </FormHint>
          )}
          <UtmInputWrap
            $fullWidth={valueInputFullWidth}
            $textCentered={valueInputFullWidth}
          >
            <Input
              name="utmQuantity"
              type="text"
              inputMode="decimal"
              placeholder="Ej. 2,05"
              value={utmValue}
              onChange={onAmountChange}
              disabled={disabled}
            />
          </UtmInputWrap>
          {valueError && <FieldError>{valueError}</FieldError>}
        </Field>
      )}

      {showValue && isImm && (
        <Field>
          {showValueLabel && (
            <FormLabel>Porcentaje del sueldo mínimo (IMM)</FormLabel>
          )}
          {showValueHint && (
            <FormHint>
              Típico: 40% por un hijo, o 30% por cada hijo si son dos o más.
              {immValue
                ? ` IMM vigente: ${formatCurrency(immValue)}.`
                : ''}
            </FormHint>
          )}
          <UtmInputWrap
            $fullWidth={valueInputFullWidth}
            $textCentered={valueInputFullWidth}
          >
            <Input
              name="immPercentage"
              type="text"
              inputMode="decimal"
              placeholder="Ej. 40"
              value={immPct}
              onChange={onPercentageChange}
              disabled={disabled}
            />
          </UtmInputWrap>
          {estimatedImmAmount != null && (
            <FormHint>
              Estimado actual: {formatCurrency(estimatedImmAmount)} al mes.
            </FormHint>
          )}
          {valueError && <FieldError>{valueError}</FieldError>}
        </Field>
      )}
    </Root>
  )
}

PensionConfigForm.defaultProps = {
  percentage: '',
  amount: '',
  methodError: '',
  valueError: '',
  showMethod: true,
  showMethodLabel: true,
  showValue: true,
  immValue: null,
  disabled: false,
  valueInputFullWidth: false,
  showValueLabel: true,
  showValueHint: true,
  onPercentageChange: undefined,
  onAmountChange: undefined,
}

PensionConfigForm.propTypes = {
  method: PropTypes.oneOf([
    METHOD.UTM,
    METHOD.IMM,
    METHOD.FIXED_AMOUNT,
  ]).isRequired,
  percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onMethodChange: PropTypes.func.isRequired,
  onPercentageChange: PropTypes.func,
  onAmountChange: PropTypes.func,
  methodError: PropTypes.string,
  valueError: PropTypes.string,
  showMethod: PropTypes.bool,
  showMethodLabel: PropTypes.bool,
  showValue: PropTypes.bool,
  immValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
  valueInputFullWidth: PropTypes.bool,
  showValueLabel: PropTypes.bool,
  showValueHint: PropTypes.bool,
}

export default PensionConfigForm
